import { Alert, Button } from 'antd';
import React, { FC } from 'react';

interface AOPLoadFailedProps {
	/** What could not be read, in the screen's own words. */
	message: string;
	/** Reads it again. Every screen that shows this has something to re-ask. */
	onRetry: () => void;
	isRetrying?: boolean;
	/** A sentence under the message, where "this does not mean X" is worth saying. */
	description?: React.ReactNode;
}

/**
 * A read that failed, where a screen would otherwise have shown its empty
 * state.
 *
 * The two are different claims and were being made together: a failed read
 * left the Radar screens showing "Could not load what Radar has run" directly
 * above "Radar has not run this procedure yet" — one of them a statement about
 * this procedure that nothing had established. The failure takes the place of
 * the empty state rather than sitting on top of it, and carries the retry,
 * because the alternative was leaving the tab and coming back.
 *
 * An error beside content that did load is a different case and stays inline:
 * there the rows are the answer and the message is about the page that did not
 * arrive.
 */
export const AOPLoadFailed: FC<AOPLoadFailedProps> = ({
	message,
	onRetry,
	isRetrying,
	description,
}) => (
	<Alert
		type="error"
		showIcon
		message={message}
		description={
			<div className="flex flex-col items-start gap-s">
				{description ? (
					<span className="bodyregular text-character-primary">{description}</span>
				) : null}
				<Button loading={isRetrying} onClick={onRetry}>
					Try again
				</Button>
			</div>
		}
	/>
);

export default AOPLoadFailed;
